import { supabase, STORAGE_BUCKETS } from './supabase.js';

/**
 * Get public URL for a file in the public bucket (nft-previews)
 * @param {string} path - The path to the file in the public bucket
 * @returns {string} The public URL for accessing the file
 */
export function posterUrl(path) {
  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKETS.PREVIEWS)
    .getPublicUrl(path);
  
  return publicUrl;
}

/**
 * Generate a signed URL for a model file with 1-hour expiry
 * @param {string} bucket - The bucket name ('nft-originals' or 'nft-previews')
 * @param {string} key - The object key/path in the bucket
 * @returns {Promise<string>} The signed URL for accessing the model
 */
export async function modelUrl(bucket, key) {
  // If it's already a full URL, return it directly
  if (key.startsWith('http')) {
    return key;
  }
  
  // If it's from the public bucket, return public URL
  if (bucket === STORAGE_BUCKETS.PREVIEWS) {
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(key);
    return publicUrl;
  }
  
  // For private bucket, create signed URL
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(key, 3600); // 1 hour expiry
  
  if (error || !data?.signedUrl) {
    throw error ?? new Error(`No signedUrl generated for ${bucket}/${key}`);
  }
  
  return data.signedUrl;
}

/**
 * HEAD check a URL to verify it's accessible before rendering
 * @param {string} url - The URL to check
 * @returns {Promise<void>} Promise that resolves if URL is accessible, rejects otherwise
 */
export async function verifyUrl(url) {
  const res = await fetch(url, { method: 'HEAD' });
  if (!res.ok) {
    throw new Error(`URL verification failed: ${res.status} ${res.statusText}`);
  }
}
